/**
 * PDP Articles Section — displays related articles/content below product details.
 *
 * Articles come from product custom attribute `c_module3Assets` (array of content IDs)
 * with an optional heading from `c_module3Heading`.
 *
 * Content source is site-dependent:
 * - HSASTORE / WDH → Builder.io (model: "learning-center-article")
 * - FSASTORE → SFCC BM content assets (via ShopperExperience API)
 *
 * Reference: sfcc-website/org_hec/cartridge/templates/default/product/components/articlesPDP.isml
 *            sfcc-website/org_hec/cartridge/templates/default/product/components/contentTile.isml
 *            hec-composable/hec/builder/blocks/article/articleCard.jsx
 */
import { ArrowLeft, ArrowRight } from "lucide-react";

import { Skeleton } from "~/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/hec/components/ui/carousel";
import { WavyDivider } from "~/hec/components/ui/wavy-divider";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ArticleTile {
  id: string;
  name: string;
  imageUrl?: string;
  description?: string;
  url: string;
}

interface PDPArticlesProps {
  heading?: string;
  articles: ArticleTile[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Max articles to display — matches SFRA carousel (~4 visible at desktop) */
const MAX_VISIBLE = 4;

function ArticleTileLink({ article }: { article: ArticleTile }) {
  return (
    <a
      href={article.url}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-border text-center"
      data-testid={`pdp-article-${article.id}`}
    >
      <div className="mb-10 aspect-[20/13] w-full overflow-hidden md:aspect-[1.618/1]">
        {article.imageUrl ? (
          <img
            src={article.imageUrl}
            alt={article.name}
            width={400}
            height={260}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-50">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              className="h-10 w-10 text-neutral-300"
              aria-hidden="true"
            >
              <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
        )}
      </div>
      <h3 className="mb-5 hyphens-auto break-words px-3 pb-1 pt-4 text-xl font-black leading-[1.5] text-emphasis md:text-2xl">
        {article.name}
      </h3>
      <span className="mb-[35px] mt-auto px-3 font-grotesque text-sm font-bold text-emphasis underline group-hover:no-underline">
        Read Article
      </span>
    </a>
  );
}

/**
 * Skeleton for the article-card grid. Mirrors the final card dimensions
 * (`h-[25rem]` mobile / `h-[18.75rem]` sm+, image 2/5 + text 3/5) and the
 * optional heading bar so the section reserves the same vertical space
 * during streaming.
 */
export function PDPArticlesSkeleton({
  count = MAX_VISIBLE,
}: { count?: number } = {}) {
  return (
    <section
      className="pdp-articles mt-8 w-full"
      aria-label="Related articles loading"
    >
      {/* Optional heading bar (matches `c_module3Heading` row in <PDPArticles />) */}
      <div className="mb-4 flex items-center gap-3">
        <div className="h-px flex-1 bg-accent" />
        <Skeleton className="h-6 w-48" />
        <div className="h-px flex-1 bg-accent" />
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="flex h-[25rem] flex-col overflow-hidden rounded-lg border border-border sm:h-[18.75rem]"
          >
            <Skeleton className="h-2/5 w-full rounded-none" />
            <div className="flex flex-1 flex-col gap-2 px-3 py-4">
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="mt-auto h-3 w-20 self-center" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PDPArticles({ heading, articles }: PDPArticlesProps) {
  if (articles.length === 0) return null;

  const visible = articles.slice(0, MAX_VISIBLE);
  // Match production: arrows only when desktop (2 visible) needs to scroll.
  // Mobile uses swipe-only — production Slick behaves the same.
  const showArrows = visible.length > 2;

  return (
    <section className="mt-12 w-full">
      <div className="text-center">
        <WavyDivider className="mx-auto" />
        <h2 className="mb-6 mt-2 text-3xl font-bold leading-normal text-emphasis">
          {heading || "Learn More"}
        </h2>
      </div>
      <Carousel
        opts={{ align: "start", loop: true }}
        className="relative"
        data-testid="pdpArticles-Carousel"
      >
        <CarouselContent
          className="ml-0"
          data-testid="pdpArticles-CarouselContent"
        >
          {visible.map(article => (
            <CarouselItem
              key={article.id}
              slidesVisible="2"
              slidesVisibleMobile="1"
              slidePadding="3"
              slidePaddingMobile="2"
              className="min-[768px]:basis-1/2 basis-full px-2.5 pb-[30px] lg:px-2.5"
              data-testid="pdpArticles-CarouselItem"
            >
              <ArticleTileLink article={article} />
            </CarouselItem>
          ))}
        </CarouselContent>
        {showArrows && (
          <>
            <CarouselPrevious
              overlapContent
              buttonIcon={ArrowLeft}
              className="top-2/5 left-0 z-20 flex h-12 w-12 translate-y-0 cursor-pointer items-center justify-center border border-emphasis bg-white text-emphasis opacity-100 lg:-left-5"
              iconAttributes={{ className: "h-5 w-5", strokeWidth: "1.5" }}
              data-testid="pdpArticles-CarouselPrevious"
            />
            <CarouselNext
              overlapContent
              buttonIcon={ArrowRight}
              className="top-2/5 right-0 z-20 flex h-12 w-12 translate-y-0 cursor-pointer items-center justify-center border border-emphasis bg-white text-emphasis opacity-100 lg:-right-5"
              iconAttributes={{ className: "h-5 w-5", strokeWidth: "1.5" }}
              data-testid="pdpArticles-CarouselNext"
            />
          </>
        )}
      </Carousel>
    </section>
  );
}
